/**
 * The requester's own profile — Step 22.
 *
 * Draft-first, like every other free-text form in the product: nothing
 * leaves the page until Save, and typed content registers as dirty so
 * navigating away asks first.
 *
 * The email address is shown but NOT editable. It is the identity a magic
 * link authenticates and the address every ticket threads on, so changing it
 * from here would let someone move their own account. That is a support
 * action with an audit trail, and the page says so rather than silently
 * disabling the field.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, UserRound } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';

export function PortalProfilePage() {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalProfile,
    queryFn: portalApi.profile,
  });
  const [draft, setDraft] = useState<{
    name: string;
    jobTitle: string;
    department: string;
    officeLocation: string;
  } | null>(null);
  /** Step 178 — the directory owns this record, so the form is a display. */
  const locked = Boolean((data as { directoryOwned?: boolean } | undefined)?.directoryOwned);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const active =
    draft ??
    (data
      ? {
          name: data.name ?? '',
          jobTitle: data.jobTitle ?? '',
          department: data.department ?? '',
          officeLocation: data.officeLocation ?? '',
        }
      : null);
  const dirty =
    !!data &&
    !!active &&
    (active.name !== (data.name ?? '') ||
      active.jobTitle !== (data.jobTitle ?? '') ||
      active.department !== (data.department ?? '') ||
      active.officeLocation !== (data.officeLocation ?? ''));
  useDirty(dirty);

  const save = useMutation({
    mutationFn: () => portalApi.updateProfile(active!),
    onSuccess: (res) => {
      if (!res.ok) {
        setSaveError(res.error ?? 'Could not save your details');
        return;
      }
      setDraft(null);
      setSaveError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: qk.portalProfile });
    },
  });

  if (isPending) return <Skeleton className="h-72 w-full max-w-xl" />;
  if (isError)
    return (
      <div className="max-w-xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load your details</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
          <UserRound className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-lg font-semibold">Your details</h1>
          <p className="text-xs text-muted-foreground">
            These help the support team reach you and route your requests.
          </p>
        </div>
      </div>

      <div className="space-y-4 rounded-lg border bg-card p-6">
        {/**
         * Step 178 — read-only when the directory owns the record.
         *
         * Kai: *"lets not allow that if they're entra synced as obviously it'll
         * get overriden nightly anyway."* Exactly — and the old form did not
         * know that, so somebody could change their job title, see it save, and
         * find it reverted by morning with nothing to explain why. An input
         * that cannot hold its value is worse than a field that says who owns
         * it.
         */}
        {locked && (
          <div className="flex items-start gap-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Your name and work details come from your organisation's directory and are kept up to date
              there, so they cannot be changed here. Ask the support team if something is wrong.
            </span>
          </div>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium">Email address</label>
          <Input value={data!.email} disabled />
          <p className="mt-1 text-xs text-muted-foreground">
            This is how you sign in and how your tickets reach you, so it can only be changed by the support
            team.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Name</label>
          <Input
            value={active!.name}
            disabled={locked}
            onChange={(e) => setDraft({ ...active!, name: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">Job title</label>
            <Input
              value={active!.jobTitle}
              disabled={locked}
              onChange={(e) => setDraft({ ...active!, jobTitle: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Department</label>
            <Input
              value={active!.department}
              disabled={locked}
              onChange={(e) => setDraft({ ...active!, department: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Office / location</label>
          <Input
            value={active!.officeLocation}
            disabled={locked}
            onChange={(e) => setDraft({ ...active!, officeLocation: e.target.value })}
          />
        </div>

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
        {!locked && (
          <div className="flex items-center justify-end gap-3 border-t pt-4">
            {saved && <span className="text-xs text-muted-foreground">Saved</span>}
            {dirty && <span className="text-xs text-amber-600 dark:text-amber-500">Unsaved changes</span>}
            <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
